// Phase 10 — UserDashboard (Football Products kit rewrite).
//
// Live dashboard showing today's predictions across all products.
// Reads directly from the hooks that power each product page.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePowerballWeek } from "../../powerball/usePowerballWeek.ts";
import { useSinglesSession } from "../../singles/useSinglesSession.ts";
import { useRolloverPicks } from "../../rollover/useRolloverPicks.ts";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import {
  FpHead, FpHero, HeroChip, DefList, FpScoreboard, FpCard,
  FpLegRow, FpAwait, FpSkeleton, FpSeam, FpDisclaimer,
  fmtOdds, fmtNum, fmtKick, leagueCountry, countryFromCode,
} from "../../pages/football/_fpkit.tsx";

function todayStr(): string {
  return new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export function UserDashboard(): JSX.Element {
  // ── Product hooks ──────────────────────────────────────────
  const pb = usePowerballWeek();
  const singles = useSinglesSession();
  const rollover = useRolloverPicks();

  const loading = pb.loading || singles.loading || rollover.loading;

  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({ t1HasPicks: !!(pb.pb10 || singles.slots.length > 0 || rollover.picks.length > 0) });

  // ── Derived stats ──────────────────────────────────────────
  const pbLegs = (pb.pb10?.selections?.length ?? 0) + (pb.mega20?.selections?.length ?? 0) + (pb.mixedMega?.selections?.length ?? 0);
  const pbProducts = [pb.pb10, pb.mega20, pb.mixedMega].filter((s) => s?.status === "published").length;
  const singlesSlots = singles.slots.filter((s) => s.fixture_id);
  const rolloverTiers = useMemo(() => {
    const tiers = new Set<string>();
    for (const p of rollover.picks) tiers.add(p.tier);
    return tiers.size;
  }, [rollover.picks]);
  const totalPicks = pbLegs + singlesSlots.length + rollover.picks.length;

  // ── Skeleton ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="fp">
        <FpHead kicker="DASHBOARD" title={`Loading · ${todayStr()}`} />
        <FpSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div className="fp">
      <FpHead
        kicker="DASHBOARD"
        title={todayStr()}
        status={totalPicks > 0 ? `${totalPicks} picks live` : "Scanning"}
        live={totalPicks > 0}
      />

      {/* Hero */}
      <FpHero
        kicker="TODAY'S INTELLIGENCE"
        title={<>Daily<br /><em>Command Center</em></>}
        lede="All products in one view. Every pick surfaced by the agents — click any row to read the reasoning."
        chips={
          <>
            {pbProducts > 0 && <HeroChip tone="accent">{pbProducts} Powerball</HeroChip>}
            {singlesSlots.length > 0 && <HeroChip tone="accent">{singlesSlots.length} Singles</HeroChip>}
            {rolloverTiers > 0 && <HeroChip tone="accent">{rolloverTiers} Rollover tiers</HeroChip>}
            {totalPicks === 0 && <HeroChip>Awaiting picks</HeroChip>}
          </>
        }
        aside={
          <DefList rows={[
            { k: "Total picks", v: String(totalPicks) },
            { k: "Powerball legs", v: String(pbLegs) },
            { k: "Singles", v: `${singlesSlots.length} slot${singlesSlots.length === 1 ? "" : "s"}` },
            { k: "Rollover tiers", v: String(rolloverTiers) },
            { k: "Session", v: todayStr() },
          ]} />
        }
      />

      {/* Scoreboard */}
      <FpScoreboard tiles={[
        {
          label: "Powerball",
          value: pbProducts > 0 ? `${pbProducts}/3` : DASH,
          sub: pbProducts > 0 ? `${pbLegs} legs published` : "Awaiting pipeline",
          live: pbProducts > 0,
        },
        {
          label: "Singles",
          value: singlesSlots.length > 0 ? String(singlesSlots.length) : DASH,
          sub: singlesSlots.length > 0 ? `${singlesSlots.length} pick${singlesSlots.length === 1 ? "" : "s"} today` : "Awaiting agents",
          live: singlesSlots.length > 0,
        },
        {
          label: "Rollover",
          value: rolloverTiers > 0 ? `${rolloverTiers} tier${rolloverTiers === 1 ? "" : "s"}` : DASH,
          sub: rolloverTiers > 0 ? `${rollover.picks.length} legs across tiers` : "Awaiting pipeline",
          live: rolloverTiers > 0,
        },
      ]} />

      <FpSeam />

      {/* ── Singles ─────────────────────────────────────────── */}
      {singlesSlots.length > 0 && (
        <FpCard
          kicker="SINGLES CHAIN"
          title={`${singlesSlots.length} pick${singlesSlots.length === 1 ? "" : "s"} today`}
          action={<Link to="/singles" style={{ color: "var(--fp-gold-text)", textDecoration: "none", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em" }}>VIEW →</Link>}
          padding={false}
        >
          {singlesSlots.map((slot) => (
            <FpLegRow
              key={slot.id}
              index={slot.slot_number}
              fixture={[slot.home_team, slot.away_team].filter(Boolean).join(" vs ") || "TBD"}
              league={slot.league_short || slot.league_name || undefined}
              market={slot.market || "TBD"}
              selection={slot.selection || "TBD"}
              odds={slot.odds ?? slot.target_odds}
              prob={slot.model_probability ?? undefined}
              reasoning={slot.agent_reasoning}
              confidence={slot.confidence}
              country={countryFromCode(slot.country_code) ?? leagueCountry(slot.league_name, slot.league_short)}
              kickoffAt={slot.kickoff_at}
              kickoff={slot.kickoff_at ? fmtKick(slot.kickoff_at) : undefined}
            />
          ))}
        </FpCard>
      )}

      {/* ── Powerball PB10 ──────────────────────────────────── */}
      {pb.pb10 && pb.pb10.selections.length > 0 && (
        <FpCard
          kicker="POWERBALL · PB10"
          title={`${pb.pb10.selections.length} legs · @${fmtOdds(pb.pb10.combined_odds)}`}
          action={<Link to="/powerball" style={{ color: "var(--fp-gold-text)", textDecoration: "none", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em" }}>VIEW →</Link>}
          padding={false}
        >
          {pb.pb10.selections.slice(0, 5).map((leg, i) => (
            <FpLegRow
              key={leg.id}
              index={i + 1}
              fixture={leg.match_name || "TBD"}
              league={leg.league}
              market={leg.market}
              selection={leg.pick}
              odds={leg.odds}
              prob={leg.model_probability ?? undefined}
              layer={leg.layer}
              country={countryFromCode(leg.country_code) ?? leagueCountry(leg.league, leg.league_short)}
              kickoffAt={leg.kickoff_at}
              kickoff={leg.kickoff_at ? fmtKick(leg.kickoff_at) : undefined}
              reasoning={`${leg.match_name}: ${leg.pick} in the ${leg.layer} layer at @${leg.odds.toFixed(2)}.`}
              formScore={leg.form_score}
              bestPlatform={leg.best_platform}
              bestOdds={leg.best_odds}
            />
          ))}
          {pb.pb10.selections.length > 5 && (
            <div style={{ padding: "10px 20px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.06em" }}>
              +{pb.pb10.selections.length - 5} more legs — <Link to="/powerball" style={{ color: "var(--fp-gold-text)" }}>view full slip</Link>
            </div>
          )}
        </FpCard>
      )}

      {/* ── Rollover Tiers ──────────────────────────────────── */}
      {rollover.picks.length > 0 && (
        <FpCard
          kicker="ROLLOVER"
          title={`${rolloverTiers} tier${rolloverTiers === 1 ? "" : "s"} · ${rollover.picks.length} legs`}
          action={<Link to="/rollover" style={{ color: "var(--fp-gold-text)", textDecoration: "none", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em" }}>VIEW →</Link>}
          padding={false}
        >
          {rollover.picks.slice(0, 6).map((leg, i) => (
            <FpLegRow
              key={i}
              index={i + 1}
              fixture={leg.match || "TBD"}
              league={leg.league || undefined}
              market={leg.market || "Match Result"}
              selection={leg.pick}
              odds={leg.odds}
              confidence={leg.confidence || undefined}
              reasoning={leg.reasoning}
            />
          ))}
          {rollover.picks.length > 6 && (
            <div style={{ padding: "10px 20px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-tertiary)", letterSpacing: "0.06em" }}>
              +{rollover.picks.length - 6} more legs — <Link to="/rollover" style={{ color: "var(--fp-gold-text)" }}>view all tiers</Link>
            </div>
          )}
        </FpCard>
      )}

      {/* ── Empty state ─────────────────────────────────────── */}
      {totalPicks === 0 && (
        <>
          <FpAwait
            kicker="SCANNING"
            title="Agents are assembling today's picks"
            body="Singles, Powerball, and Rollover pipelines run daily. Picks appear here as soon as the agents publish them."
            icon="target"
          />
          <DoctrineSection
            probabilityPicks={doctrineProbPicks}
            valuePicks={doctrineValuePicks}
            isFullFallback
            substrateEmpty={substrateEmpty}
            substrateWeek={substrateWeek}
          />
        </>
      )}

      <FpDisclaimer />
    </div>
  );
}

const DASH = "—";
